import type { HarvestLog } from '../types/production'

import { toLocalCalendarDayKey } from './dateNormalization'
import { farmTimeZone } from './farmTime'

/**
 * Canonical daily harvest rollup for one (lot, calendar date) pair.
 *
 * Built by iterating every `HarvestLog` whose `recordedAt` resolves to the
 * same calendar day and whose `lot` matches, then SUMMING `harvestKg` and
 * `harvestCount` across all contributing logs.
 *
 * Semantic difference from `dailyFishStockRollup` (SUM-PER-DAY vs
 * LATEST-VALUE-WINS):
 *
 * - `FishStockLog.countedStock` values are competing snapshots of the same
 *   population — the LATEST count supersedes prior counts.
 * - `HarvestLog.harvestKg` values are independent extraction events that
 *   accumulate throughout the day. Two harvests on the same day should
 *   TOTAL together, not compete. Same lot + same day = additive.
 *
 * v2.2 T3-D parallel to FSL: when any `HarvestLog` events exist for a
 * given `(lot, date)`, this rollup IS the canonical daily harvest signal.
 * Consumers should prefer `totalHarvestKg` over any single event's
 * `harvestKg` and over the row's stored harvest fields for that day. Rows
 * without any matching event fall back to the stored value.
 */
export type DailyHarvestRollup = {
  /** Calendar date key in `YYYY-MM-DD` form (farm-local interpretation). */
  date: string
  /** Trimmed lot label, or empty string when the source log omitted it. */
  lot: string
  /** SUM of `harvestKg` across contributing logs, formatted with 3 decimal places. */
  totalHarvestKg: string
  /** SUM of `harvestCount` across contributing logs, integer-formatted as a string. */
  totalHarvestCount: string
  /**
   * `'count'` iff every contributing log used `method === 'count'`; otherwise
   * `'estimation'`. Any estimation weakens the trust signal for the day.
   */
  aggregatedMethod: 'count' | 'estimation'
  /** IDs of every log that contributed, in input encounter order. */
  logIds: string[]
  /** Number of contributing logs. Equal to `logIds.length`. */
  eventCount: number
  /** Latest `recordedAt` value among contributing logs (raw ISO string). */
  latestRecordedAt: string
}

const toCalendarDateKey = (raw: string | undefined | null, timeZone?: string): string => {
  // Group by the *local* calendar day, matching the mortality + weight-sample
  // + fish-stock rollup contract and the finding #7 + #47 timezone rules.
  return toLocalCalendarDayKey(raw, timeZone)
}

const toLotKey = (raw: string | undefined | null): string => {
  if (typeof raw !== 'string') return ''
  return raw.trim()
}

const parseRecordedAtMs = (raw: string | undefined | null): number | null => {
  if (typeof raw !== 'string' || !raw) return null
  const ms = new Date(raw).getTime()
  return Number.isFinite(ms) ? ms : null
}

const parseHarvestKg = (raw: unknown): number => {
  const num = typeof raw === 'number' ? raw : Number(String(raw ?? '').replaceAll(',', '.').trim())
  return Number.isFinite(num) ? num : 0
}

const parseHarvestCount = (raw: unknown): number => {
  if (raw === undefined || raw === null || raw === '') return 0
  const num = typeof raw === 'number' ? raw : Number(String(raw).replaceAll(',', '').trim())
  return Number.isFinite(num) ? num : 0
}

/** Compose the `(date, lot)` lookup key used by the rollup map. */
export const buildDailyHarvestRollupKey = (date: string, lot: string): string => {
  return `${date}::${lot}`
}

type AggregatorState = {
  sumHarvestKg: number
  sumHarvestCount: number
  allCount: boolean
  latestRecordedAtMs: number
  latestRecordedAtRaw: string
}

/**
 * Group `HarvestLog` events by `(calendar date, lot)` and SUM `harvestKg`
 * plus `harvestCount` across every log in the group.
 *
 * The returned map is keyed by `buildDailyHarvestRollupKey(date, lot)`.
 * Logs whose `recordedAt` cannot be parsed into a calendar date (or into a
 * millisecond timestamp) are skipped entirely — they cannot participate in
 * a daily total.
 *
 * The function is pure and side-effect free. It does not mutate the input.
 */
export const rollupHarvestLogsByLotDay = (
  logs: readonly HarvestLog[]
): Map<string, DailyHarvestRollup> => {
  const result = new Map<string, DailyHarvestRollup>()
  const state = new Map<string, AggregatorState>()

  logs.forEach((log) => {
    // Findings #85: a record's day is its farm's day.
    const date = toCalendarDateKey(log.recordedAt, farmTimeZone(log.farmId))
    if (!date) return

    const recordedAtMs = parseRecordedAtMs(log.recordedAt)
    if (recordedAtMs === null) return

    const lot = toLotKey(log.lot)
    const key = buildDailyHarvestRollupKey(date, lot)
    const kg = parseHarvestKg(log.harvestKg)
    const count = parseHarvestCount(log.harvestCount)
    const method: 'count' | 'estimation' = log.method === 'estimation' ? 'estimation' : 'count'
    const recordedAtRaw = typeof log.recordedAt === 'string' ? log.recordedAt : ''

    const existing = result.get(key)
    const currentState = state.get(key)

    if (!existing || !currentState) {
      state.set(key, {
        sumHarvestKg: kg,
        sumHarvestCount: count,
        allCount: method === 'count',
        latestRecordedAtMs: recordedAtMs,
        latestRecordedAtRaw: recordedAtRaw
      })
      result.set(key, {
        date,
        lot,
        totalHarvestKg: kg.toFixed(3),
        totalHarvestCount: String(count),
        aggregatedMethod: method,
        logIds: [log.id],
        eventCount: 1,
        latestRecordedAt: recordedAtRaw
      })
      return
    }

    currentState.sumHarvestKg += kg
    currentState.sumHarvestCount += count
    if (method !== 'count') currentState.allCount = false
    if (recordedAtMs > currentState.latestRecordedAtMs) {
      currentState.latestRecordedAtMs = recordedAtMs
      currentState.latestRecordedAtRaw = recordedAtRaw
    }

    existing.logIds.push(log.id)
    existing.eventCount = existing.logIds.length
    existing.totalHarvestKg = currentState.sumHarvestKg.toFixed(3)
    existing.totalHarvestCount = String(currentState.sumHarvestCount)
    existing.aggregatedMethod = currentState.allCount ? 'count' : 'estimation'
    existing.latestRecordedAt = currentState.latestRecordedAtRaw
  })

  return result
}

/**
 * Convenience lookup: get the rollup that matches a `(rawDate, lot)` pair,
 * normalizing the date the same way as `rollupHarvestLogsByLotDay`.
 */
export const findDailyHarvestRollup = (
  rollup: ReadonlyMap<string, DailyHarvestRollup>,
  rawDate: string,
  lot: string | undefined | null
): DailyHarvestRollup | undefined => {
  const date = toCalendarDateKey(rawDate)
  if (!date) return undefined
  return rollup.get(buildDailyHarvestRollupKey(date, toLotKey(lot)))
}
